import { 
  collection, 
  doc,
  getDoc,
  setDoc,
  updateDoc, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, retryOperation } from '../config';

const COLLECTION_NAME = 'productSettings';
const CATEGORIES_DOC_ID = 'categories';
const TYPES_DOC_ID = 'productTypes';

export interface ProductSettings {
  id: string;
  data: string[];
  updatedAt: Date | Timestamp;
}

/**
 * Get categories from Firestore
 * @param type - 'food' or 'retail'
 */
export const getCategories = async (type: 'food' | 'retail'): Promise<string[]> => {
  try {
    return await retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, CATEGORIES_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as Record<string, string[]>;
        return data[type] || [];
      }
      
      // If document doesn't exist, return empty array
      return [];
    });
  } catch (error) {
    console.error(`Error getting ${type} categories:`, error);
    return []; // Return empty array on error
  }
};

/**
 * Save categories to Firestore
 * @param type - 'food' or 'retail'
 * @param categories - Array of category names
 */
export const saveCategories = async (type: 'food' | 'retail', categories: string[]): Promise<void> => {
  try {
    await retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, CATEGORIES_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // Update existing document
        const data = docSnap.data() as Record<string, any>;
        await updateDoc(docRef, {
          [type]: categories,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new document
        await setDoc(docRef, {
          [type]: categories,
          updatedAt: serverTimestamp()
        });
      }
    });
  } catch (error) {
    console.error(`Error saving ${type} categories:`, error);
    throw error;
  }
};

/**
 * Get product types from Firestore
 */
export const getProductTypes = async (): Promise<string[]> => {
  try {
    return await retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, TYPES_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.types || [];
      }
      
      // If document doesn't exist, return empty array
      return [];
    });
  } catch (error) {
    console.error('Error getting product types:', error);
    return []; // Return empty array on error
  }
};

/**
 * Save product types to Firestore
 * @param types - Array of product type names
 */
export const saveProductTypes = async (types: string[]): Promise<void> => {
  try {
    await retryOperation(async () => {
      const docRef = doc(db, COLLECTION_NAME, TYPES_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // Update existing document
        await updateDoc(docRef, {
          types: types,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new document
        await setDoc(docRef, {
          types: types,
          updatedAt: serverTimestamp()
        });
      }
    });
  } catch (error) {
    console.error('Error saving product types:', error);
    throw error;
  }
}; 